import React from 'react';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import { useCartStore, Product } from '../../../store/cart-store';
import { Link } from 'react-router-dom';
import { FILES_BASE_URL } from '../../../config';

interface CartProps {
  onProceedToCheckout?: () => void;
}

const Cart: React.FC<CartProps> = () => {
  const { items, removeItem, updateQuantity, clearCart, getTotalPrice } = useCartStore();
  const totalPrice = getTotalPrice();

  const totalLocalPrice = items.reduce((total, item) => {
    const localPrice = item.aditionalData[0]?.price_local_currency || 0;
    return total + localPrice * (item.quantity || 1);
  }, 0);

  const totalPoints = items.reduce((total, item) => {
    const points = item.aditionalData[0]?.loyalty_points || 0;
    return total + points * (item.quantity || 1);
  }, 0);

  const handleQuantityChange = (product: Product, quantity: number) => {
    updateQuantity(product._id, quantity);
  };

  if (items.length === 0) {
    return (
      <Container>
        <Row className="justify-content-center">
          <Col md={8}>
            <Card className="bg-white border-0 rounded-3 mb-4">
              <Card.Body className="p-4 text-center">
                <i className="ri-shopping-cart-2-line fs-70 text-muted mb-3"></i>
                <h3>Carrito vacío</h3>
                <p className="text-muted">Agrega algunos productos al carrito para verlos aquí.</p>
                <Link to="/store">
                  <Button variant="primary">Continuar comprando</Button>
                </Link>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Row>
      <Col lg={8}>
        <Card className="bg-white border-0 rounded-3 mb-4">
          <Card.Body className="p-4">
            <h4 className="fw-semibold mb-4">Carrito de compras</h4>

            {items.map((product) => {
              const aditionalData = product.aditionalData[0];
              const price = aditionalData?.price_usdt || 0;
              const imageUrl = product.images.length > 0
                ? `${FILES_BASE_URL}/${product.images[parseInt(product.mainImage) || 0]}`
                : '/images/placeholder.png';

              return (
                <div key={product._id} className="d-flex border-bottom pb-4 mb-4">
                  <div className="me-3" style={{ width: '100px', height: '100px' }}>
                    <img
                      src={imageUrl}
                      alt={product.title}
                      className="img-fluid rounded-3 object-fit-cover w-100 h-100"
                    />
                  </div>

                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 className="fw-semibold">{product.title}</h5>
                      <Button
                        variant="link"
                        className="p-0 text-danger"
                        onClick={() => removeItem(product._id)}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </Button>
                    </div>

                    <p className="text-muted small mb-3">{product.shortDescription}</p>

                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleQuantityChange(product, (product.quantity || 1) - 1)}
                        >
                          -
                        </Button>
                        <Form.Control
                          type="number"
                          min="1"
                          value={product.quantity || 1}
                          onChange={(e) => handleQuantityChange(product, parseInt(e.target.value) || 1)}
                          className="mx-2 text-center"
                          style={{ width: '60px', height: '25px' }}
                        />
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleQuantityChange(product, (product.quantity || 1) + 1)}
                        >
                          +
                        </Button>
                      </div>

                      <div className="text-end">
                        <div className="fw-semibold">
                          ${(aditionalData?.price_local_currency * (product.quantity || 1)).toLocaleString()} {product.currency}
                        </div>
                        {price && price !== 0 ? (
                          <div className="text-muted small">
                            ${(price * (product.quantity || 1)).toFixed(2)} USDT
                          </div>
                        ) : null}
                        <div className="text-muted small">
                          {product.aditionalData[0].loyalty_points} Puntos
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="d-flex justify-content-between mt-4">
              <Link to="/store">
                <Button variant="outline-primary">
                  <i className="ri-arrow-left-line me-2"></i>
                  Continuar comprando
                </Button>
              </Link>
              <Button variant="outline-danger" onClick={clearCart}>
                <i className="ri-delete-bin-line me-2"></i>
                Limpiar carrito
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col lg={4}>
        <Card className="bg-white border-0 rounded-3 mb-4">
          <Card.Body className="p-4">
            <h4 className="fw-semibold mb-4">Resumen del pedido</h4>

            <div className="d-flex justify-content-between mb-2">
              <span>Subtotal {items[0]?.currency}</span>
              <span className="fw-semibold">${totalLocalPrice.toLocaleString()} {items[0]?.currency}</span>
            </div>
            
            {Number(totalPrice.toFixed(2))  > 0 && (
              <div className="d-flex justify-content-between mb-2">
                <span>Subtotal USDT</span>
                <span className="fw-semibold">${totalPrice.toFixed(2)} USDT</span>
              </div>
            )}
           

            <hr />

            <div className="d-flex justify-content-between mb-2">
              <span className="fw-semibold">Total {items[0]?.currency}</span>
              <span className="fw-bold fs-5">${totalLocalPrice.toLocaleString()} {items[0]?.currency}</span>
            </div>

            {Number(totalPrice.toFixed(2))  > 0 && (
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-semibold">Total USDT</span>
                <span className="fw-bold fs-5">${totalPrice.toFixed(2)} USDT</span>
              </div>
            )}
           
            <div className="d-flex justify-content-between mb-4">
              <span className="fw-semibold">Total Puntos</span>
              <span className="fw-bold fs-5">{totalPoints} Puntos</span>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default Cart;
